import type { CityConfig } from "../city-template/types";

export const doverDeConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Delaware Vital Records (Birth, Death, Marriage)", href: "https://www.dhss.delaware.gov/dhss/dph/ss/vitalstats.html", description: "Official Delaware Division of Public Health vital records office in Dover" },
    { label: "Kent County Clerk of the Peace — Marriage &amp; Vital Records", href: "https://www.co.kent.de.us/", description: "Kent County Clerk of the Peace — local marriage and vital records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Delaware DPH for online vital record orders" },
  ],
  city: "Dover",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "dover-de",
  route: "/dover-de",
  title: "Emergency Passport Dover DE | Expedited Passport Renewal Delaware Capital | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Dover, DE — as fast as 24 hours. Fast passport renewal for Kent County, Dover Air Force Base, and central Delaware travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Dover, DE",
  heroSubtitle: "Need an emergency passport in Dover? Flying out of Philadelphia or Baltimore? Fast Passport Center helps Delaware's capital city and Kent County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Dover%20Delaware%20state%20capitol%20building%20with%20its%20distinctive%20silver%20dome%20gleaming%20in%20warm%20afternoon%20sunlight%2C%20surrounded%20by%20manicured%20green%20lawns%20and%20mature%20oak%20trees%2C%20historic%20colonial-era%20architecture%2C%20clear%20blue%20sky%20with%20soft%20white%20clouds%2C%20clean%20professional%20government%20center%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20photorealistic%20style&width=1800&height=700&seq=dover-de-hero-001&orientation=landscape",
  heroImageAlt: "Dover Delaware state capitol building with its distinctive silver dome on a sunny afternoon",
  geoPosition: "39.1582;-75.5244",
  geoPlacename: "Dover, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "600 Arch St Suite 4200, Philadelphia, PA 19106",
    distance: "Approximately 80–90 minutes from Dover via I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Dover", zips: "19901–19904" },
    { name: "Smyrna", zips: "19977" },
    { name: "Milford", zips: "19963" },
    { name: "Harrington", zips: "19952" },
    { name: "Camden", zips: "19934" },
    { name: "Wyoming", zips: "19934" },
    { name: "Magnolia", zips: "19962" },
    { name: "Felton", zips: "19943" },
    { name: "Woodside", zips: "19980" },
    { name: "Bowers Beach", zips: "19946" },
    { name: "Cheswold", zips: "19936" },
    { name: "Clayton", zips: "19938" },
    { name: "Kenton", zips: "19955" },
    { name: "Hartly", zips: "19953" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for urgent Dover and central Delaware departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no 90-min drive to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wilmington, DE", route: "/wilmington-de" },
    { name: "Fast passport services in Newark, DE", route: "/newark-de" },
    { name: "Emergency passport in Philadelphia", route: "/philadelphia" },
    { name: "Delaware passport services overview", route: "/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Dover remotely via secure registered courier. We do not have a walk-in office in Dover — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for central Delaware travelers without the 90-minute drive to Philadelphia.",
  fpcOfficeAddress: "1700 Market St, Suite 1005, Philadelphia, PA 19103",
  fpcOfficeDistance: "Serving all of Kent County, Delaware",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Kent County and the greater Dover area. From the state government complex near Legislative Mall to Dover Air Force Base, from Smyrna in the north to Milford and Harrington in the south — we cover all of central Delaware.",
  nearbyCitiesIntro: "We serve all Dover communities and nearby central Delaware towns including Smyrna, Milford, Harrington, Camden, Wyoming, Magnolia, Felton, Cheswold, Clayton, and Kenton. If you are in Kent County, we have you covered.",
  howItWorksIntro: "Most Dover travelers complete everything online and ship documents overnight. No need to drive 90 minutes to Philadelphia or 120 minutes to Washington DC. Our process is designed for Delaware state employees, military families, and residents who need fast, reliable passport results.",
  mistakesIntro: "Dover travelers include Delaware government employees, Dover Air Force Base military personnel, and Bayhealth Medical Center staff — many with last-minute international travel needs. Here are the most common passport mistakes we see.",
  testimonialsIntro: "Dover and Kent County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Dover? Here are the most common ones we hear from Kent County travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  dmvWebsite: "https://www.dmv.de.gov",
  dmvLabel: "Delaware DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 80–90 min to Philadelphia", "Full day commitment — plan accordingly"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Dover travelers with immediate departure needs — as fast as 24 hours for urgent international flights out of PHL or BWI.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "No drive to Philadelphia required"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Dover adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application assistance for Dover and Kent County residents.",
      bullets: ["Acceptance facility locator in Dover", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Dover and central Delaware residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — especially helpful for Dover AFB military families.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Military family documentation support"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction after marriage, divorce, or legal name changes in Delaware.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Dover DE", description: "Urgent passport processing for Dover and Kent County travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Dover DE", description: "Fast passport renewal for Dover Delaware residents with a valid expired passport." },
    { name: "Military Passport Dover AFB", description: "Expedited passport services for Dover Air Force Base military personnel and their families." },
    { name: "Child Passport Dover DE", description: "Minor passport services for Dover families with children under 16." },
  ],
  testimonials: [
    {
      name: "Col. James T. (Ret.)",
      location: "Dover, DE",
      rating: 5,
      text: "My TDY orders came in 72 hours before departure. Fast Passport Center had my emergency passport ready in 36 hours. Essential service for Dover AFB military families.",
      service: "Emergency Passport",
    },
    {
      name: "Angela M.",
      location: "Smyrna, DE",
      rating: 5,
      text: "State government work trip to Brussels with a week's notice. They handled my expedited renewal perfectly. No driving to Philadelphia — they handled everything from Dover.",
      service: "Passport Renewal",
    },
    {
      name: "Chris R.",
      location: "Milford, DE",
      rating: 5,
      text: "Lost my passport two weeks before a family trip to the Caribbean. Got a replacement in 3 days. Incredibly smooth process — they walked us through everything.",
      service: "Lost Passport",
    },
    {
      name: "Donna K.",
      location: "Camden, DE",
      rating: 5,
      text: "Kent County resident here — never knew you could get a passport this fast without driving to Philadelphia. Used them twice now and both times were seamless.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "dover-de-1",
      name: "Dover Post Office — Main Branch",
      type: "USPS Passport Acceptance Facility",
      address: "56 S DuPont Hwy",
      city: "Dover",
      state: "DE",
      zip: "19901",
      phone: "(302) 678-0600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Dover",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Dover. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-de-2",
      name: "Smyrna Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 N Church St",
      city: "Smyrna",
      state: "DE",
      zip: "19977",
      phone: "(302) 653-2480",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving Smyrna and northern Kent County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Smyrna and Clayton area residents. Limited passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-de-3",
      name: "Milford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "17 SE 2nd St",
      city: "Milford",
      state: "DE",
      zip: "19963",
      phone: "(302) 422-3025",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving Milford and southern Kent County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves the Kent-Sussex border area. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-de-4",
      name: "Harrington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "103 Commerce St",
      city: "Harrington",
      state: "DE",
      zip: "19952",
      phone: "(302) 398-4242",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "Serving Harrington and rural Kent County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Harrington and Felton area residents. Very limited hours — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-de-5",
      name: "Kent County Clerk of Peace",
      type: "County Clerk Passport Acceptance",
      address: "555 Bay Rd",
      city: "Dover",
      state: "DE",
      zip: "19901",
      phone: "(302) 744-2314",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Kent County government center, Dover",
      website: "https://www.co.kent.de.us/",
      websiteLabel: "Kent County Clerk",
      notes: "Full-service county facility. Accepts walk-ins but appointments are preferred for faster processing.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 80–90 min drive to Philadelphia", fpc: "No — ship from your Dover home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Dover photo shops often produce generic ID photos. Passport photos must be exactly 2x2 inches with a pure white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using outdated form versions are the most common rejection reasons at Kent County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government passport fees are updated periodically. Dover applicants frequently show up with incorrect payment and must reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel for Emergency",
      description: "The Philadelphia Passport Agency requires confirmed travel within 14 days. Dover travelers need flight confirmation before requesting emergency appointments.",
    },
    {
      icon: "ri-shield-star-line",
      title: "Military Orders but No Civilian Passport",
      description: "Dover AFB personnel sometimes assume military travel documents cover all international travel. Civilian family members and personal travel always require a regular U.S. passport.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Dover travelers often underestimate the time needed. Standard processing takes 6–13 weeks. Start expedited service at least 4–6 weeks before departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location in Dover." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Dover address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Dover, DE?", a: "Fast Passport Center offers emergency passport processing in Dover in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (80–90 minutes away) or other State Department facilities — without you needing to make the drive. This is the fastest reliable option for Dover and Kent County travelers with urgent international flights." },
    { q: "Does Dover have a passport agency?", a: "No — Delaware does not have a regional passport agency. The closest is the Philadelphia Passport Agency, approximately 80–90 minutes from Dover. Fast Passport Center's courier service eliminates the need for Dover residents to make this drive, handling all submission and delivery on your behalf." },
    { q: "Can I get a same-day passport from Dover?", a: "Same-day passports are only available at the Philadelphia Passport Agency for documented life-or-death emergencies with travel within 72 hours. For all other urgent travel from Dover, Fast Passport Center's 24–48 hour emergency processing is the fastest guaranteed option — no drive required." },
    { q: "How do Dover Air Force Base personnel get emergency passports?", a: "Dover AFB military personnel and their families can use Fast Passport Center's expedited service just like any civilian traveler. Personal travel and civilian family member passports always require a standard U.S. passport regardless of military status. We understand the unique scheduling demands of military travel and offer fast 24–48 hour processing." },
    { q: "What airports do Dover travelers use for international flights?", a: "Dover residents most commonly fly internationally from Philadelphia International Airport (PHL), approximately 80 minutes north on I-95. Baltimore-Washington International (BWI) is also accessible, about 90 minutes south on US-13 and I-97. Always verify your passport is valid for at least 6 months beyond your return date." },
    { q: "Can I get an expedited passport for state government travel from Dover?", a: "Yes. Fast Passport Center provides expedited passports for Delaware state government employees and contractors in Dover on short notice. Our 2–9 business day expedited service is designed for last-minute work travel, and our 24–48 hour emergency service handles truly urgent departures." },
    { q: "Where can I get passport photos in Dover?", a: "CVS Pharmacy locations in Dover, Walgreens on Bay Road, FedEx Office, and Walmart Supercenter in Dover all offer passport photo services. We recommend confirming the exact 2x2 inch white background requirement before your photos are taken." },
    { q: "How much does expedited passport service cost from Dover?", a: "Government fees are fixed: $130 for an adult passport book plus $60 government expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your Dover travel timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Dover", address: "1225 S Governors Ave, Dover, DE 19904", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Dover", address: "399 S Bay Rd, Dover, DE 19901", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "Walmart Supercenter — Dover", address: "1118 S Governor's Ave, Dover, DE 19904", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "FedEx Office — Dover", address: "1399 N DuPont Hwy, Dover, DE 19901", hours: "Mon–Fri: 8AM–8PM, Sat: 9AM–6PM", website: "https://www.office.fedex.com/" },
    { name: "UPS Store — Dover", address: "562 N DuPont Hwy, Dover, DE 19901", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
  ],
  dmvLocations: [
    { name: "DMV — Dover", address: "303 Transportation Circle, Dover, DE 19901", hours: "Mon–Fri: 8AM–4:30PM", note: "Main Kent County DMV. REAL ID and driver's license services. Appointment required.", featured: true },
    { name: "DMV — Milford", address: "122 NE Front St, Milford, DE 19963", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves southern Kent County and Sussex County border residents.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "80–90 miles from Dover",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for Dover travelers. 80–90 minute drive north on I-95. Major international hub.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "85–95 miles from Dover",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Popular alternative for Dover residents, especially for domestic connections and budget airlines.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "45 miles from Dover",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Closest airport to Dover for domestic flights. Limited international service.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Airport",
      featured: false,
    },
  ],
};